import { useState, useEffect, useCallback } from 'react';
import NumberInputBox, { InputState } from 'components/Shared/SearchFilter/NumberInputBox';
import RangeSlider from 'components/Shared/SearchFilter/Slider';
import styles from './styles';

export interface InputSliderBlockProps {
  onChange?: (value: InputSliderBlockValue) => void;
  minAllowed: number;
  maxAllowed: number;
  initialMin: number;
  initialMax: number;
  minPlaceholder: string;
  maxPlaceholder: string;
  pushable: number | boolean;
  step: number;
  hasThousandSeparator?: boolean;
  disableMinChange?: boolean;
  getMinPrefix?: (inputState: InputState) => string | undefined;
  getMinSuffix?: (inputState: InputState) => string | undefined;
  getMaxPrefix?: (inputState: InputState) => string | undefined;
  getMaxSuffix?: (inputState: InputState) => string | undefined;
}

export interface InputSliderBlockValue {
  min: number;
  max: number;
}

function roundDownToStep(value: number, step: number): number {
  return Math.floor(value / step) * step;
}

export default function InputSliderBlock({
  onChange,
  minAllowed,
  maxAllowed,
  initialMin,
  initialMax,
  minPlaceholder,
  maxPlaceholder,
  pushable,
  step,
  hasThousandSeparator,
  disableMinChange = false,
  getMinPrefix,
  getMinSuffix,
  getMaxPrefix,
  getMaxSuffix,
}: InputSliderBlockProps): JSX.Element {
  const [currentValue, setCurrentValue] = useState<InputSliderBlockValue>({
    min: initialMin,
    max: initialMax,
  });
  useEffect(() => {
    setCurrentValue({
      min: initialMin,
      max: initialMax,
    });
  }, [initialMin, initialMax]);

  const handleChange = useCallback(
    (change: Partial<InputSliderBlockValue>, commit = true) => {
      setCurrentValue((prevValue) => {
        const newValue = { ...prevValue, ...change };
        if (commit && !areValuesEqual({ min: initialMin, max: initialMax }, newValue)) {
          onChange?.(newValue);
        }
        return newValue;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialMin, initialMax]
  );

  const handleMinChange = useCallback((newMin?: number) => {
    if (newMin === undefined) return;

    const roundedMin = roundDownToStep(newMin, step);
    if (roundedMin < minAllowed) {
      handleChange({ min: minAllowed });
    } else if (roundedMin > maxAllowed) {
      handleChange({
        min: currentValue.max,
        max: maxAllowed,
      });
    } else if (roundedMin > currentValue.max) {
      handleChange({
        min: currentValue.max,
        max: roundedMin,
      });
    } else {
      handleChange({ min: roundedMin });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMaxChange = useCallback((newMax?: number) => {
    if (newMax === undefined) return;

    const roundedMax = roundDownToStep(newMax, step);

    if (roundedMax > maxAllowed) {
      handleChange({ max: maxAllowed });
    } else if (roundedMax < minAllowed) {
      handleChange({
        max: currentValue.min,
        min: minAllowed,
      });
    } else if (roundedMax < currentValue.min) {
      handleChange({
        max: currentValue.min,
        min: roundedMax,
      });
    } else {
      handleChange({ max: roundedMax });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSliderChange = useCallback(
    (change: number[]) => {
      const shouldUpdateMin = Number(change[0]) !== currentValue.min;
      const shouldUpdateMax = Number(change[1]) !== currentValue.max;

      if (shouldUpdateMin || shouldUpdateMax) {
        const newValue: Partial<InputSliderBlockValue> = {};
        if (shouldUpdateMin) {
          newValue.min = Number(change[0]);
        }
        if (shouldUpdateMax) {
          newValue.max = Number(change[1]);
        }
        handleChange(newValue, false);
      }
    },
    [currentValue.min, currentValue.max, handleChange]
  );

  function handleSliderAfterChange(change: number[]): void {
    handleChange({
      min: change[0],
      max: change[1],
    });
  }

  const validateMinNumberInputBoxChange = useCallback(
    (newValue: number | undefined) => {
      return !(
        newValue === undefined ||
        (currentValue.min === minAllowed && newValue < minAllowed) ||
        roundDownToStep(newValue, step) === currentValue.min
      );
    },
    [currentValue.min, minAllowed, step]
  );

  const validateMaxNumberInputBoxChange = useCallback(
    (newValue: number | undefined) => {
      return !(
        newValue === undefined ||
        (currentValue.max === maxAllowed && newValue > maxAllowed) ||
        roundDownToStep(newValue, step) === currentValue.max
      );
    },
    [currentValue.max, maxAllowed, step]
  );

  return (
    <span className="input-slider-block-container">
      <span className="number-input-box-container">
        {!disableMinChange ? (
          <>
            <NumberInputBox
              id="min-input"
              value={currentValue.min}
              placeholder={minPlaceholder}
              onChange={handleMinChange}
              hasThousandSeparator={hasThousandSeparator}
              getPrefix={getMinPrefix}
              getSuffix={getMinSuffix}
              validateChange={validateMinNumberInputBoxChange}
            />
            <p>to</p>
          </>
        ) : (
          <p className="disabled-min-value">
            {minAllowed} {getMinSuffix?.({ numericValue: minAllowed, stringValue: '' })}
          </p>
        )}
        <NumberInputBox
          id="max-input"
          value={currentValue.max}
          placeholder={maxPlaceholder}
          onChange={handleMaxChange}
          hasThousandSeparator={hasThousandSeparator}
          getPrefix={getMaxPrefix}
          getSuffix={getMaxSuffix}
          validateChange={validateMaxNumberInputBoxChange}
        />
      </span>
      <span className="slider-outer-container">
        <RangeSlider
          pushable={pushable}
          disableMinChange={disableMinChange}
          min={minAllowed}
          max={maxAllowed}
          defaultValue={[minAllowed, maxAllowed]}
          value={[currentValue.min, currentValue.max]}
          step={step}
          onChange={handleSliderChange}
          onAfterChange={handleSliderAfterChange}
        />
      </span>
      <style jsx>{styles}</style>
    </span>
  );
}

function areValuesEqual(a: InputSliderBlockValue, b: InputSliderBlockValue): boolean {
  // use `==` for js type-coercion to guard against comparing string vs number
  // eslint-disable-next-line eqeqeq
  return a.min == b.min && a.max == b.max;
}
